import { useState, useMemo, useEffect } from "react";
import { HeroBanner } from "../components/HeroBanner";
import { FoodCategories } from "../components/FoodCategories";
import { OrderControls } from "../components/OrderControls";
import { MenuGrid } from "../components/MenuGrid";
import { OrderSummary } from "../components/OrderSummary";
import { OrderStatusNotification } from "../components/OrderStatusNotification";
import { Footer } from "../components/Footer";
import { useApp } from "../App";
import { getAllCategories, getAllMenuItems } from "@/services/menuService";
import { DeliveryTypeModal } from "@/components/DeliveryTypeModal";
import { CartNotification } from "@/components/CartNotification";
import { useVisibleCategory } from "@/hooks/useVisibleCategory";
export function HomePage() {
  const {
    cartItems,
    orderMode,
    isDeliveryModeSelected,
    restaurant,
    setOrderMode,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    setDeliveryModeSelected,
  } = useApp();

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [highlightedCategory, setHighlightedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState<
    | "confirmed"
    | "preparing"
    | "cooking"
    | "ready"
    | "out-for-delivery"
    | "delivered"
  >("confirmed");

  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(
    !isDeliveryModeSelected,
  );

  const [showCartToast, setShowCartToast] = useState(false);
  const [cartItemName, setCartItemName] = useState("");
  const [cartItemQty, setCartItemQty] = useState(1);

  // Function to handle modal close
  const handleCloseModal = () => {
    setIsDeliveryModalOpen(false);
    setDeliveryModeSelected(true);
  };
  const [menuItems, setMenuItems] = useState([]);
  const activeCategory = useVisibleCategory(menuItems);

  useEffect(() => {
    if (activeCategory && !searchQuery && selectedCategory === "All") {
      setHighlightedCategory(activeCategory);
    } else {
      setHighlightedCategory(selectedCategory);
    }
  }, [activeCategory, searchQuery, selectedCategory]);

  const fetchMenu = async () => {
    try {
      const response = await getAllMenuItems();
      setMenuItems(response);
      setSelectedCategory("All");
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    if (isDeliveryModeSelected != true) {
      if (
        restaurant?.isDeliveryModeEnabled === true &&
        restaurant?.isPickupModeEnabled === true
      ) {
        setIsDeliveryModalOpen(true);

        // Push new history state so back button closes modal
        window.history.pushState({ modal: true }, "");
      } else {
        setIsDeliveryModalOpen(false); // Optional, depends on UX
      }
    }
  }, [restaurant]);
  // Load items on screen mount
  useEffect(() => {
    fetchMenu();
  }, []);
  // Filter menu items based on categoryName and search query
  const normalize = (text: string = "") =>
    text
      .toLowerCase()
      .replace(/\s+/g, "") // remove spaces
      .replace(/s$/, "") // remove trailing plural "s"
      .replace(/[^a-z0-9]/g, ""); // remove symbols
  // Filter menu items based on categoryName and search query
  const filteredItems = useMemo(() => {
    let filtered = menuItems;

    // 🔍 SEARCH HAS PRIORITY (search across all items)
    if (searchQuery && searchQuery.trim() !== "") {
      let query = normalize(searchQuery);
      filtered = menuItems.filter((item: any) => {
        const name = normalize(item.name);
        const description = normalize(item.description);
        const category = normalize(item.categoryName);

        return (
          name.includes(query) ||
          description.includes(query) ||
          category.includes(query)
        );
      });
    }
    // 📂 CATEGORY FILTER (only when no search)
    else if (selectedCategory !== "All") {
      if (selectedCategory === "Popular Items") {
        filtered = filtered.filter((item: any) => item.isPopular);
      } else {
        filtered = filtered.filter(
          (item: any) => String(item.categoryId) === String(selectedCategory),
        );
      }
    }
    console.log("ca", selectedCategory);

    // 🔃 SORT
    filtered = filtered.sort((a: any, b: any) => {
      const categoryCompare = a.categoryName.localeCompare(b.categoryName);
      if (categoryCompare !== 0) return categoryCompare;
      return a.name.localeCompare(b.name);
    });

    return filtered;
  }, [selectedCategory, searchQuery, menuItems]);

  // Simulate order status updates when items are added to cart
  const handleAddToCart = (item: any) => {
    addToCart(item);
    setShowCartToast(true);
    setCartItemName(item?.name);
    setCartItemQty(item?.quantity);

    // Show notification for order in progress
    if (cartItems.length === 0) {
      // First item added
      setNotificationStatus("confirmed");
      // Currently Disabled
      setShowNotification(false);
      // Simulate status progression
      setTimeout(() => {
        setNotificationStatus("preparing");
      }, 3000);

      setTimeout(() => {
        setNotificationStatus("cooking");
      }, 8000);

      setTimeout(() => {
        setNotificationStatus("ready");
      }, 15000);
    }
  };

  // Stack history maintaine
  useEffect(() => {
    const handleBack = () => {
      if (isDeliveryModalOpen) {
        setIsDeliveryModalOpen(false);
        return;
      }
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [isDeliveryModalOpen]);
  return (
    <>
      <div className="hidden lg:block">
        <HeroBanner />

        <div className="animate-slide-in-up">
          <OrderControls
            orderMode={orderMode}
            setOrderMode={setOrderMode}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>
      </div>

      {/* Food Categories Section - Sticky */}
      <div
        id="menu-section"
        className="sticky top-0 z-40 bg-white shadow-sm animate-slide-in-up"
      >
        <FoodCategories
          selectedCategory={selectedCategory}
          highlightedCategory={highlightedCategory}
          onCategorySelect={(category) => {
            setSelectedCategory(category);
            setHighlightedCategory(category);
          }}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </div>

      <main className="max-w-7xl mx-auto mobile-padding py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Menu Items */}
          <div className="lg:col-span-3">
            {/* <div className="mb-6 sm:mb-8 animate-fade-in-up">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 font-display">
                {selectedCategory === "All" ? "Our Menu" : selectedCategory}
              </h2>
              <p className="text-gray-600 text-sm sm:text-base">
                {filteredItems.length} delicious dish
                {filteredItems.length !== 1 ? "es" : ""} available
              </p>
            </div> */}

            <div className="animate-stagger">
              <MenuGrid
                items={filteredItems}
                onAddToCart={(item) => {
                  handleAddToCart(item);
                  window.history.pushState({ modal: true }, "");
                }}
                cartItems={cartItems}
                onUpdateQuantity={updateCartQuantity}
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1 animate-slide-in-right">
            <OrderSummary
              cartItems={cartItems}
              onUpdateQuantity={updateCartQuantity}
              onRemoveItem={removeFromCart}
              onAddToCart={addToCart}
              orderMode={orderMode}
            />
          </div>
        </div>
      </main>

      {/* Order Status Notification */}
      <OrderStatusNotification
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        status={notificationStatus}
        estimatedTime="25-35 min"
      />
      {/* Item added notification */}
      <CartNotification
        isVisible={showCartToast}
        itemName={cartItemName}
        quantity={cartItemQty}
        onClose={() => setShowCartToast(false)}
      />

      {/* Delivery Type Modal */}
      <DeliveryTypeModal
        isOpen={isDeliveryModalOpen}
        onClose={() => {
          handleCloseModal();
          // Remove the pushed state
          if (window.history.state?.modal) {
            window.history.back();
          }
        }}
        orderMode={orderMode}
        onOrderModeChange={(type) => {
          setOrderMode(type);
          // Push new history state so back button closes modal
          window.history.pushState({ modal: true }, "");
        }}
      />
      <Footer />
    </>
  );
}
